import { z } from "zod";
import type { PaginatedData } from "@core/types/TApiResponse";
import type { TDocFormat } from "@modules/docs/types";

// ─── Schemas ──────────────────────────────────────────────────────────────────

export const ApprovalStatusSchema = z.preprocess(
  (val) => (typeof val === "string" ? val.toLowerCase() : val),
  z.enum(["draft", "pending", "in_review", "approved", "rejected"]),
);

const MemberSummarySchema = z.object({
  first_name: z.string(),
  last_name: z.string(),
  email: z.string(),
  picture: z.string().nullable(),
  licence: z.enum(["active", "inactive"]).optional(),
});

export const ApprovalDocItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  format: z.string(),
  file_type: z.string(),
  size: z.number(),
  url: z.string().nullish(),
  current_version: z.number().nullable(),
  description: z.string().nullable(),
  status: z.string(),
  folder_id: z.string(),
  uploaded_by: z.string(),
  uploaded_at: z.string(),
  updated_at: z.string(),
  approval_status: z.string().nullable(),
  approval_id: z.string().nullable(),
});

export const ApprovalItemSchema = z.object({
  id: z.string(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  created_by: z.string().nullable(),
  updated_by: z.string().nullable(),
  name: z.string(),
  description: z.string(),
  submitter_id: z.string(),
  reviewer_id: z.string(),
  status: ApprovalStatusSchema,
  rejection_reasons: z.array(z.string()).nullable(),
  due_date: z.string(),
  doc_id: z.string(),
  project_id: z.string(),
  submitter: MemberSummarySchema,
  reviewer: MemberSummarySchema,
  doc: ApprovalDocItemSchema.nullish(),
});

// ─── Domain types (inferred) ──────────────────────────────────────────────────

export type TApprovalStatus = z.infer<typeof ApprovalStatusSchema>;
export type TApprovalDocItem = z.infer<typeof ApprovalDocItemSchema>;
export type TApprovalItem = z.infer<typeof ApprovalItemSchema>;

export type TApprovalListData = PaginatedData<TApprovalItem>;
export type TApprovalDocItemListData = PaginatedData<TApprovalDocItem>;

// ─── Payload types (plain — no schema needed) ─────────────────────────────────

export type TApprovalQueryParams = {
  page: number;
  limit: number;
  search?: string;
  submitted_to_me?: boolean | undefined;
  role: "project_admin" | "member";
};

export type TApprovalDocQueryParams = {
  format: TDocFormat;
  search?: string;
};
